// ============================================================================
// shared/api/cacheSesion
// Cache en memoria (por SESIÓN) para catálogos que casi no cambian durante la sesión
// (companías/sucursales/sedes del usuario). Cada página del panel los re-consultaba en
// CADA navegación (UsuarioXSede, ListarCompaniaSucursal...), sumando round-trips a la BD
// remota de DEV. Con este cache la misma API no se vuelve a pedir mientras siga vigente.
//
// - Clave incluye el TOKEN -> aísla por sesión/usuario (un re-login usa otra clave).
// - Se cachea la PROMESA -> las llamadas concurrentes comparten la misma request (dedup).
// - Sólo se conserva un resultado VÁLIDO (esValido); errores/undefined se descartan para
//   reintentar. TTL corto para acotar staleness tras altas/ediciones de catálogos.
//
// PERSISTENCIA (ítem 13 / §8.3 fila 2.5): además del cache en memoria, el RESULTADO válido
// se guarda en `sessionStorage` para que sobreviva a un F5/reload (antes cada recarga
// re-disparaba UsuarioXSede/ListarCompaniaSucursal/sedes). Guardrails de seguridad:
//   - La clave ya incluye el token -> aislado por sesión; el prefijo `sgcache::` acota qué
//     purgar. `sessionStorage` es por-pestaña y se borra al cerrarla.
//   - Se persiste la MISMA `expira` del fetch original (no se renueva el TTL al rehidratar).
//   - Se PURGA en logout y en mutaciones de catálogos (limpiarCacheSesion).
//   - Sólo pasa por aquí catálogo NO sensible (companías/sucursales/sedes); NUNCA permisos,
//     credenciales ni tokens de terceros. No es el límite de seguridad (ese es el backend).
//   - Si `sessionStorage` no está disponible (modo privado/quota), degrada a sólo-memoria.
// ============================================================================

type Entrada = { expira: number; valor: Promise<any> };

const cache = new Map<string, Entrada>();

// Prefijo de las entradas en sessionStorage (permite purgarlas sin tocar otras claves).
const PREFIJO = "sgcache::";

// 5 min: cubre ráfagas de navegación (el caso lento) sin quedar desactualizado por mucho
// tiempo si se agrega/edita una compañía/sucursal/sede.
const TTL_MS = 5 * 60 * 1000;

// Lee una entrada persistida VÁLIDA (no expirada) o undefined. Nunca lanza.
const leerPersistido = (clave: string): { valor: any; expira: number } | undefined => {
  try {
    const raw = sessionStorage.getItem(PREFIJO + clave);
    if (!raw) return undefined;
    const obj = JSON.parse(raw);
    if (!obj || typeof obj.expira !== "number" || obj.expira <= Date.now()) {
      sessionStorage.removeItem(PREFIJO + clave);
      return undefined;
    }
    return { valor: obj.valor, expira: obj.expira };
  } catch {
    return undefined; // storage no disponible / JSON corrupto -> sólo-memoria
  }
};

// Persiste un resultado válido con su expiración. Nunca lanza (quota/modo privado -> noop).
const guardarPersistido = (clave: string, valor: any, expira: number): void => {
  try {
    sessionStorage.setItem(PREFIJO + clave, JSON.stringify({ valor, expira }));
  } catch {
    /* quota / modo privado / valor no serializable: degradar a sólo-memoria */
  }
};

export const conCacheSesion = <T>(
  clave: string,
  fetcher: () => Promise<T>,
  esValido: (valor: T) => boolean = (v) => v != null,
): Promise<T> => {
  const ahora = Date.now();
  const hit = cache.get(clave);
  if (hit && hit.expira > ahora) return hit.valor as Promise<T>;

  // Rehidratar desde sessionStorage (sobrevive F5) antes de ir a la red. Se repuebla la
  // memoria con la expira ORIGINAL para no renovar el TTL en cada recarga.
  const persistido = leerPersistido(clave);
  if (persistido && esValido(persistido.valor as T)) {
    const p = Promise.resolve(persistido.valor as T);
    cache.set(clave, { expira: persistido.expira, valor: p });
    return p;
  }

  const valor = fetcher();
  const expira = ahora + TTL_MS;
  // No conservar resultados inválidos (para reintentar en la próxima llamada); persistir
  // sólo los válidos.
  valor
    .then((v) => {
      if (!esValido(v)) { cache.delete(clave); return; }
      guardarPersistido(clave, v, expira);
    })
    .catch(() => cache.delete(clave));
  cache.set(clave, { expira, valor });
  return valor;
};

// Invalida todo el cache de catálogos (memoria + copia persistida). Llamar tras un
// alta/edición/baja que cambie los catálogos, o al cerrar sesión.
export const limpiarCacheSesion = (): void => {
  cache.clear();
  // Purgar también las entradas persistidas (higiene de logout / cambio de catálogos).
  try {
    const aBorrar: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(PREFIJO)) aBorrar.push(k);
    }
    aBorrar.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    /* storage no disponible: nada que purgar */
  }
};

// Envuelve la promesa de una MUTACIÓN de catálogos (alta/edición/baja de compañía/sucursal/
// sede) para invalidar el cache cuando la operación fue exitosa (status 1). Así los selects
// vuelven a reflejar el cambio sin esperar el TTL. No altera el valor devuelto.
export const invalidarCatalogosTras = <T>(p: Promise<T>): Promise<T> => {
  p.then((r: any) => { if (r?.status === 1) limpiarCacheSesion(); }).catch(() => {});
  return p;
};
